import * as Sentry from "@sentry/react";
import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  routeName?: string;
}

interface State {
  error: Error | null;
}

/**
 * Local error boundary for individual routes.
 * Shows the actual error message (helps diagnose) and reports to Sentry,
 * without bringing down the whole shell.
 */
export default class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, {
      tags: { route: this.props.routeName ?? "unknown" },
      extra: { componentStack: info.componentStack },
    });
    // eslint-disable-next-line no-console
    console.error(`[RouteErrorBoundary:${this.props.routeName}]`, error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-lg w-full border border-border rounded-lg bg-card p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-2">Une erreur est survenue</h2>
          <p className="text-sm text-muted-foreground mb-4">
            {this.state.error.message || "Erreur inconnue"}
          </p>
          {this.state.error.stack && (
            <pre className="text-[11px] bg-muted/50 rounded p-3 mb-4 max-h-48 overflow-auto whitespace-pre-wrap break-all">
              {this.state.error.stack}
            </pre>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => this.setState({ error: null })}>
              Réessayer
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => (window.location.href = "/dashboard")}
            >
              Retour
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
